// src/features/attachment/logic.ts
// Framework-agnostic business logic for the Attachment Manager.
// Zero React imports — pure TypeScript functions.

import { attachmentApi } from '@core/api';
import type { RequestOptions } from '@core/api/client';
import type { Attachment, AttachmentProcessingStatus, DeleteAttachmentsBulkResponse } from '@core/types/attachment';
import { formatFileSize, getFileExtension, isImageMimeType } from '@/features/chat/logic';

export { formatFileSize, getFileExtension, isImageMimeType };

// ---------------------------------------------------------------------------
// MIME-prefix filter chips. ListAttachments' generated endpoint typed its
// `params` as the generic PaginatedRequest rather than the full
// ListAttachmentsRequest, so only fields the ApiClient's query-string
// flattener handles unambiguously (single scalars, top-level arrays) are
// safe to send through it — `mime_type_prefix` is a plain string field and
// filters real-world files (image/*, video/*, audio/*) cleanly.
// ---------------------------------------------------------------------------

export const MIME_PREFIX_FILTERS: { value: string; labelKey: string }[] = [
  { value: '', labelKey: 'attachment.manager.filter.all' },
  { value: 'image/', labelKey: 'attachment.manager.filter.image' },
  { value: 'video/', labelKey: 'attachment.manager.filter.video' },
  { value: 'audio/', labelKey: 'attachment.manager.filter.audio' },
];

export function attachmentId(a: Attachment): string {
  return a.entity?.id ?? '';
}

export function processingStatusVariant(status: AttachmentProcessingStatus | undefined): 'default' | 'secondary' | 'warning' | 'error' {
  switch (status) {
    case 'ATTACHMENT_PROCESSING_STATUS_COMPLETE': return 'default';
    case 'ATTACHMENT_PROCESSING_STATUS_PROCESSING':
    case 'ATTACHMENT_PROCESSING_STATUS_PENDING': return 'warning';
    case 'ATTACHMENT_PROCESSING_STATUS_FAILED': return 'error';
    default: return 'secondary';
  }
}

// A file above this size uses the multipart upload flow instead of the
// single presigned-URL flow. 20 MiB is comfortably above what a single PUT
// handles reliably in a browser tab.
export const MULTIPART_THRESHOLD_BYTES = 20 * 1024 * 1024;

// Multipart uploads are split into parts of this size (S3-style minimum is
// 5 MiB per part except the last one).
export const MULTIPART_PART_SIZE_BYTES = 8 * 1024 * 1024;

// ---------------------------------------------------------------------------
// Bulk delete — DeleteAttachmentsBulk is generated as a DELETE endpoint with
// no way to pass a request body (a corebuilder codegen gap: `client.delete()`
// takes no body parameter, and the generated wrapper drops the `data` param
// entirely). The same gap is already documented and worked around for
// resource.UnlinkResources in ChatDetailPage — same fix here: RequestOptions
// extends RequestInit, so a JSON body can be passed through `options.body`
// without touching the generated file.
// ---------------------------------------------------------------------------

export async function deleteAttachmentsBulk(attachmentIds: string[], hard = false): Promise<DeleteAttachmentsBulkResponse> {
  const options: RequestOptions = {
    body: JSON.stringify({ attachment_ids: attachmentIds, hard }),
    headers: { 'Content-Type': 'application/json' },
  };
  return attachmentApi.DeleteAttachmentsBulk(options);
}
